import React from 'react'
import styled from 'styled-components'
import BaseSpinner from 'react-loader-spinner'
import { Link } from 'react-router-dom'
import { IconArrowLeft } from '@loadsmart/icons'
import Card from 'components/Card'
import { formatDate } from 'utils/dateUtils'
import BaseButton, { Link as BaseLinkButton } from 'components/Button'
import RfpState from 'components/RfpState'
import { isAwardable } from 'utils/rfp'
import { RFP_STATE } from '../../../constants'

const LinkButton = styled(BaseLinkButton)`
  height: auto;
  width: auto;
`
const Spinner = styled(BaseSpinner)`
  padding: 0 20px;
`
const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
`
const Column = styled.div<{ grow?: number; align?: string; flex?: boolean }>`
  flex-direction: column;
  flex-basis: 100%;
  flex: 1;
  padding: 0 10px;
  ${({ grow }) => grow && `flex-grow: ${grow};`}
  ${({ align }) => align && `text-align: ${align};`}
  ${({ flex }) =>
    flex && `display: flex;`}
`
const Line = styled.hr`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.backgroundLight};
`
const Title = styled.div`
  font-weight: bold;
  font-size: 20px;
  line-height: 26px;
  margin-bottom: 5px;
`
const Button = styled(BaseButton)<{ float?: string }>`
  width: auto;
  border: none;
  text-align: center;
  margin: 5px;

  ${({ float }) =>
    float &&
    `
    float: ${float};
  `}
`
const Return = styled(Link)`
  display: flex;
  background: ${({ theme }) => theme.colors.backgroundReturnLink};
  padding: 5px;
  border-radius: 20px;
`
const ActionsWrapper = styled.div`
  display: flex;
  align-self: flex-end;
`
const StateWrapper = styled.div`
  display: flex;
  align-items: center;
`

export default function RFPInfo({
  rfp,
  isLoading = false,
  isExporting = false,
  onClickPublish,
  onClickFinish,
  onClickExport,
}: {
  rfp?: RequestForProposalDetail
  isLoading: boolean
  isExporting: boolean
  onClickPublish: () => void
  onClickFinish: () => void
  onClickExport: () => void
}) {
  const lanesCount = Object.values(rfp?.lanes || {}).length
  const isUpdating = !!rfp?.meta.isUpdating
  return (
    <Card>
      <Row>
        <Column>
          <Return to="/shipper/rfp">
            <IconArrowLeft width={24} height={24} />
          </Return>
        </Column>

        <Column grow={50}>
          {isLoading && <Spinner type="TailSpin" width={35} height={35} />}

          {rfp && (
            <>
              <Row>
                <Column grow={3}>
                  <Title>{rfp.name}</Title>
                  <div>
                    Start: {formatDate(rfp.contract_start)} - End:{' '}
                    {formatDate(rfp.contract_end)}
                  </div>
                </Column>

                <Column align="right" grow={3} flex>
                  <ActionsWrapper>
                    {rfp.state === RFP_STATE.FINALIZED && (
                      <Button
                        variant="light"
                        onClick={onClickExport}
                        disabled={isUpdating}
                      >
                        {isExporting ? (
                          <Spinner type="TailSpin" width={14} height={14} />
                        ) : (
                          'Export'
                        )}
                      </Button>
                    )}

                    {rfp.state === RFP_STATE.DRAFT && (
                      <Button
                        variant="main"
                        disabled={isUpdating}
                        onClick={onClickPublish}
                      >
                        {isUpdating ? (
                          <Spinner type="TailSpin" width={14} height={14} />
                        ) : (
                          'Publish'
                        )}
                      </Button>
                    )}

                    <LinkButton
                      variant="light"
                      to={`/shipper/rfp/${rfp?.id}/carriers`}
                    >
                      Carriers
                    </LinkButton>

                    {isAwardable(rfp?.state) && (
                      <Button
                        variant="light"
                        onClick={onClickFinish}
                        disabled={isUpdating || !rfp.submission_count}
                      >
                        {isUpdating ? (
                          <Spinner type="TailSpin" width={14} height={14} />
                        ) : (
                          'Finish & Award'
                        )}
                      </Button>
                    )}

                    <StateWrapper>
                      <RfpState state={rfp.state} />
                    </StateWrapper>
                  </ActionsWrapper>
                </Column>
              </Row>

              <Row>
                <Column>
                  <Line />
                  <div>
                    {rfp.carrier_invite_count || 0} Invited carriers -{' '}
                    {rfp.submission_count || 0} responses collected
                  </div>
                  <div>
                    Deadline: {formatDate(rfp.request_deadline)} - {lanesCount}{' '}
                    Lanes
                  </div>
                </Column>
              </Row>
            </>
          )}
        </Column>
      </Row>
    </Card>
  )
}
